import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { products, productDetails } from "../data/mock";
import { subscribe } from "../lib/api";
import { ProductCard, Ticker, Reveal, Kick } from "../components/Shared";
import Seo, { breadcrumbSchema, productSchema } from "../components/Seo";
import NotFound from "./NotFound";

export default function ProductPage() {
  const { slug } = useParams();
  const product = products.find((p) => p.slug === slug);
  const [view, setView] = useState("room");
  const [size, setSize] = useState(0);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setView("room");
    setSize(0);
    setEmail("");
  }, [slug]);

  if (!product) return <NotFound />;

  const sameDrop = products.filter((p) => p.drop === product.drop);
  const dIdx = sameDrop.findIndex((p) => p.slug === slug);
  const others = [1, 2, 3].map((n) => sameDrop[(dIdx + n) % sameDrop.length]).filter((p) => p.slug !== slug);
  const notify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await subscribe(email, `waitlist:${product.slug}`, product.title);
      toast.success(
        res.duplicate
          ? "You are already on this waitlist."
          : `Locked in. You will know the moment Drop ${product.drop} ships.`
      );
      setEmail("");
    } catch {
      toast.error("Something broke. Try again.");
    }
    setBusy(false);
  };

  return (
    <>
      <Seo
        title={product.metaTitle}
        description={product.metaDescription}
        path={`/shop/${product.slug}`}
        image={product.art}
        type="product"
        schemas={[
          productSchema(product),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Shop", path: "/shop" },
            { name: product.title, path: `/shop/${product.slug}` },
          ]),
        ]}
      />
      <div className="pdp">
        <div className="wrap">
          <div className="crumbs" style={{ marginBottom: 34 }}>
            <Link to="/">Home</Link>
            <span className="sep">/</span>
            <Link to="/shop">Shop</Link>
            <span className="sep">/</span>
            <span className="cur">{product.title}</span>
          </div>

          <div className="pdp-grid">
            <div className="pdp-media">
              <div className="pdp-main">
                <span className="pbadge">Drop {product.drop}</span>
                <img
                  src={view === "room" ? product.room : product.art}
                  alt={view === "room" ? product.altRoom : product.altArt}
                />
              </div>
              <div className="thumbs">
                <button
                  className={`thumb ${view === "room" ? "active" : ""}`}
                  onClick={() => setView("room")}
                  aria-label="View print in room"
                >
                  <img src={product.roomThumb} alt={product.altRoom} loading="lazy" />
                </button>
                <button
                  className={`thumb ${view === "art" ? "active" : ""}`}
                  onClick={() => setView("art")}
                  aria-label="View raw artwork"
                >
                  <img src={product.artThumb} alt={product.altArt} loading="lazy" />
                </button>
              </div>
            </div>

            <div className="pdp-info">
              <Kick k={product.kanji}>Drop {product.drop} · Canvas Print</Kick>
              <h1>{product.title}</h1>
              <p className="pdp-price">${product.sizes[size].price}</p>
              <p className="pdp-desc">{product.description}</p>

              <p className="pdp-label">Size (inches)</p>
              <div className="sizes">
                {product.sizes.map((s, i) => (
                  <button
                    key={s.label}
                    className={`sizebtn ${i === size ? "active" : ""}`}
                    onClick={() => setSize(i)}
                  >
                    {s.label}
                    <span className="sp">${s.price}</span>
                  </button>
                ))}
              </div>

              <div className="notifybox">
                <h3>Notify me when Drop {product.drop} ships</h3>
                <p className="nb-sub">
                  First run is in production. Join the waitlist and you get first claim before the public drop.
                </p>
                <form className="nform" onSubmit={notify} aria-label="Drop waitlist signup">
                  <input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button type="submit" disabled={busy}>
                    {busy ? "Saving..." : "Notify Me"}
                  </button>
                </form>
              </div>

              <ul className="pdp-details">
                {productDetails.map((d) => (
                  <li key={d}>
                    <Check size={16} strokeWidth={2.6} /> {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Ticker />

      <section style={{ paddingTop: 88, paddingBottom: 104 }}>
        <div className="wrap">
          <Reveal>
            <Kick k="刀">Complete the wall</Kick>
            <h2 className="sec">More from Drop {product.drop}.</h2>
          </Reveal>
          <div className="grid3" style={{ marginTop: 40 }}>
            {others.map((p, i) => (
              <ProductCard key={p.slug} p={p} delay={`d${i + 1}`} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
